<!--
// Copyright © 2025 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import Plyr from 'plyr'
  import { createEventDispatcher, onDestroy } from 'svelte'

  export let src: string
  export let name: string | undefined = undefined
  export let poster: string | undefined = undefined
  export let preload = true
  export let expectVideo = true
  // seconds to start from (timecode link); playback starts there once the metadata is known
  export let startTime: number | undefined = undefined

  let video: HTMLVideoElement
  let player: Plyr | null = null
  let reported = false

  const dispatch = createEventDispatcher()

  const options: Plyr.Options = {
    tooltips: { controls: true, seek: true },
    keyboard: { focused: true, global: true },
    controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'settings', 'fullscreen']
  }

  $: if (video != null) {
    player?.destroy()
    player = new Plyr(video, options)
  }

  $: if (src != null) {
    reported = false
  }

  function reportUnsupported (): void {
    if (reported) return
    reported = true
    dispatch('unsupported')
  }

  function handleError (): void {
    if (video?.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
      reportUnsupported()
    }
  }

  function handleLoadedMetadata (): void {
    // Container is parsed but the video track is not decodable: no error is raised, dimensions stay zero
    if (expectVideo && video.videoWidth === 0 && video.videoHeight === 0) {
      reportUnsupported()
      return
    }
    seekToStart()
  }

  let startApplied = false
  $: if (src != null) {
    startApplied = false
  }

  function seekToStart (): void {
    if (startApplied || startTime === undefined || video == null) return
    startApplied = true
    // `video.currentTime = x` would invalidate `video`: the block above then recreates Plyr, whose destroy
    // swaps the element for a clone taken before the seek, so the player shows 0:00 without controls
    const media = video
    const duration = Number.isFinite(media.duration) ? media.duration : undefined
    const target = duration !== undefined ? Math.min(startTime, Math.max(0, duration - 0.5)) : startTime
    media.currentTime = target
    dispatch('started', { requested: startTime, actual: target, duration })
    // opened by a click on the timecode; the browser may still refuse autoplay, then the user presses play
    void media.play().catch(() => {})
  }

  export function getCurrentTime (): number {
    return video?.currentTime ?? 0
  }

  onDestroy(() => {
    player?.destroy()
    player = null
  })
</script>

<div class="player">
  <video
    bind:this={video}
    {src}
    data-poster={poster}
    width="100%"
    height="100%"
    preload={preload ? 'auto' : 'none'}
    disablepictureinpicture
    on:error={handleError}
    on:loadedmetadata={handleLoadedMetadata}
    on:timeupdate
  >
    <track kind="captions" label={name ?? ''} />
  </video>
</div>

<style lang="scss">
  // plyr re-inserts a clone of the media element on destroy, keep that clone inside a node svelte owns
  .player {
    display: contents;
  }

  video {
    background: inherit;
    border-radius: inherit;
    object-fit: contain;
  }

  video::-webkit-media-controls {
    visibility: hidden;
  }

  video::-webkit-media-controls-enclosure {
    visibility: visible;
  }

  @import 'plyr/dist/plyr.css';
  :global(.plyr) {
    flex: 1;
    min-width: 10rem;
    border-radius: 0.75rem;
    width: 100%;
    height: 100%;
    --plyr-control-spacing: 0.5rem;
    --plyr-control-icon-size: 1rem;
    --plyr-video-background: transparent;
  }

  // Hide controls when video is stopped and not hovered
  :global(.plyr.plyr--stopped:not(:hover) .plyr__controls) {
    opacity: 0;
    pointer-events: none;
    transform: translateY(100%);
  }
</style>
